import { formatUSPhoneNumber } from '@user/features/GlobalSearch/ResultItem/utils'
import {
  selectContactLocations,
  selectEntityStatus,
  selectFallbackLocation,
  selectFallbackLocationStatus,
  selectLocation,
  selectMapEntityDetails,
  selectMapEntityVariant,
} from '@user/store/map'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { formatAddress } from '../../utils/formatAddress'
import { ENTITY_TYPES } from '@sales-center/common'
import { getSalesFunnelStage } from '@user/features/MapView/MultiTenantDrawer/MultiTenantDrawerContentSingle/hooks/useData'
import { HereMapClickEvent } from '@user/components/HereMap/HereMapNew/hooks/useHereMapInstance'
import { EntityLocationDTO } from '@user/store/map/typings'
import { hasViewableHistory } from '@user/utils/knockStatusFormatting'

type Props = {
  data?: HereMapClickEvent | null
  pendingCustomReason?: {
    pinId: number
    reason: string | null
  } | null
}

export function useData({
  data = null,
  pendingCustomReason = null,
}: Props = {}) {
  const selectedEntity = useSelector(selectMapEntityDetails)
  const fallbackLocation = useSelector(selectFallbackLocation)
  const entityStatus = useSelector(selectEntityStatus)
  const fallbackEntityDetailsStatus = useSelector(selectFallbackLocationStatus)
  const serviceLocation = useSelector(selectLocation)
  const variant = useSelector(selectMapEntityVariant)
  const contactLocations = useSelector(selectContactLocations)

  const nameAndCompanyDisplay = useMemo(() => {
    if (!serviceLocation) {
      return ''
    }
    const companyName = serviceLocation.company
    const name = `${serviceLocation.firstName} ${serviceLocation.lastName}`
    const items = [name, companyName].filter((x) => x && x.trim().length > 0)
    if (items.length > 1) {
      return items.join(' / ')
    } else {
      return items[0] || ''
    }
  }, [serviceLocation])

  const addressDisplay = useMemo(
    () =>
      selectedEntity && selectedEntity.entityType === ENTITY_TYPES.DraftLead
        ? formatAddress(
            selectedEntity?.street1 || '',
            selectedEntity?.street2 || '',
            selectedEntity?.city || '',
            selectedEntity?.stateAbbreviation || '',
            selectedEntity?.postalCode || '',
          )
        : serviceLocation && serviceLocation.contact
          ? formatAddress(
              serviceLocation.contact.street1,
              serviceLocation.contact.street2,
              serviceLocation.contact.city,
              serviceLocation.contact.stateAbbreviation,
              serviceLocation.contact.postalCode,
            )
          : '',

    [selectedEntity, serviceLocation],
  )

  // SH-1200: this must be the backend EntityType enum value ('Lead' |
  // 'Opportunity' | 'DraftLead'), not a display string -- it's passed
  // directly to updateKnockStatusHistoryById. 'Pending Lead' (with a space)
  // doesn't match any case in the backend's entityType switch, which was
  // silently failing reason-save for draft/pending leads edited from this
  // bubble specifically (the drawer's own useData already used 'DraftLead'
  // correctly, which is why View-then-edit worked while direct bubble edit
  // did not). Use entityTypeDisplay below for user-facing text.
  const entityType = useMemo(() => {
    switch (variant?.toLowerCase()) {
      case 'lead':
        return 'Lead'
      case 'opportunity':
        return 'Opportunity'
      case 'draftlead':
      case 'pendinglead':
        return 'DraftLead'
      default:
        return 'Unknown'
    }
  }, [variant])

  const entityTypeDisplay = useMemo(() => {
    switch (variant?.toLowerCase()) {
      case 'lead':
        return 'Lead'
      case 'opportunity':
        return 'Opportunity'
      case 'draftlead':
      case 'pendinglead':
        return 'Pending Lead'
      default:
        return 'Unknown'
    }
  }, [variant])

  const formattedPhone = useMemo(() => {
    if (!serviceLocation) {
      return ''
    }
    const phone = serviceLocation.phoneNumber
    return phone ? formatUSPhoneNumber(phone).trim() || phone : ''
  }, [serviceLocation])

  const status = useMemo(() => {
    return getSalesFunnelStage(entityType, selectedEntity?.salesFunnelStage)
  }, [entityType, selectedEntity])

  const knockStatus = useMemo(() => {
    const details = serviceLocation?.knockStatusDetails ?? {
      id: data?.knockStatusHistoryId,
      knockStatusSettingsName: data?.knockStatusSettingName,
      mostRecentNote: data?.knockStatusReason,
      createdTime: data?.knockStatusCreatedTime,
      createdByName: data?.knockStatusCreatedByName,
    }

    return {
      id: details.id ?? null,
      isDefault: details.knockStatusSettingsName === 'Do not knock',
      status: details.knockStatusSettingsName || 'None',
      note: details.mostRecentNote || null,
      createdTime: details.createdTime || null,
      createdByName: details.createdByName || null,
    }
  }, [
    serviceLocation?.knockStatusDetails,
    data?.knockStatusHistoryId,
    data?.knockStatusSettingName,
    data?.knockStatusReason,
    data?.knockStatusCreatedTime,
    data?.knockStatusCreatedByName,
  ])

  // SH-1550 -- history link for the entity bubble. Reads the entity's own service location:
  // knockStatusPreview below only resolves for standalone knock status pins.
  const canViewHistory = useMemo(() => {
    const details = serviceLocation?.knockStatusDetails
    return hasViewableHistory(
      details?.knockStatusCount ?? 0,
      Boolean(details?.knockStatusSettingsName),
    )
  }, [serviceLocation?.knockStatusDetails])

  const knockStatusLocationPinId = Number(data?.address?.knockStatusLocationId)

  const matchedContactLocation = useMemo(() => {
    if (!knockStatusLocationPinId || contactLocations.length === 0) {
      return null
    }

    // Match the bubble's KS location against the live pins. Tile-sourced pins
    // (MapPinDTO) carry knockStatusLocationId at the top level and omit the
    // nested `address`, so prefer the top-level field. Also match on the pinId
    // (`KS_{id}`), which the SearchArea projection always sets — this is the
    // robust key, since matching only on `address?.knockStatusLocationId` left
    // this null on the tile path and live KS edits never reached the open
    // bubble (SH-XXX).
    const ksPinId = `KS_${knockStatusLocationPinId}`
    return (
      contactLocations.find(
        (location: EntityLocationDTO) =>
          location.pinId === ksPinId ||
          Number(
            location.knockStatusLocationId ??
              location.address?.knockStatusLocationId,
          ) === knockStatusLocationPinId,
      ) ?? null
    )
  }, [contactLocations, knockStatusLocationPinId])

  const knockStatusPreview = useMemo(() => {
    // When a live pin matches, trust it wholesale — including a null/empty
    // reason. Falling back to `data` per-field (`?? data`) masked a cleared
    // reason with the stale click-time snapshot, so emptying the reason kept
    // showing the previous one. Only use `data` when there's no live match.
    const live = matchedContactLocation
    const status = live
      ? live.knockStatusSettingName
      : data?.knockStatusSettingName
    const baseNote = live ? live.knockStatusReason : data?.knockStatusReason
    // An inline reason edit from the history list is pushed via
    // pendingCustomReason (keyed by knockStatusLocationId) so the open bubble
    // reflects the edited active reason immediately, before the tile refetch
    // re-resolves the pin. A later create/add-history save clears it.
    const note =
      pendingCustomReason &&
      pendingCustomReason.pinId === knockStatusLocationPinId
        ? pendingCustomReason.reason
        : baseNote
    // hasHistory: a live tile match only carries a count; the click-time lookup
    // (recordsAtLocation) returns the full list, so check for an active entry.
    return {
      // SH-1817 -- the standalone knock-status-only pin needs the active
      // history row's own id so its preview bubble can show the edit-reason
      // pencil (StatusSection gates the pencil on knockStatus.id).
      id: live?.knockStatusHistoryId ?? data?.knockStatusHistoryId ?? null,
      isDefault: (status ?? '') === 'Do not knock',
      status: status ?? '',
      note: note ?? null,
      createdTime: live
        ? live.knockStatusCreatedTime
        : (data?.knockStatusCreatedTime ?? null),
      createdByName: live
        ? live.knockStatusCreatedByName
        : (data?.knockStatusCreatedByName ?? null),
      knockStatusCount: live
        ? live.knockStatusCount
        : (data?.knockStatusCount ?? null),
      hasHistory: live
        ? hasViewableHistory(
            Number(live.knockStatusCount ?? 0),
            Boolean(status),
          )
        : hasViewableHistory(
            data?.knockStatusHistory?.length ?? 0,
            Boolean(data?.knockStatusHistory?.some((h) => h.isActive)),
          ),
      hasKnockStatusLocation:
        !!knockStatusLocationPinId || !!matchedContactLocation,
    }
  }, [
    matchedContactLocation,
    knockStatusLocationPinId,
    data?.knockStatusHistoryId,
    data?.knockStatusSettingName,
    data?.knockStatusReason,
    data?.knockStatusCreatedTime,
    data?.knockStatusCreatedByName,
    data?.knockStatusCount,
    data?.knockStatusHistory,
    pendingCustomReason,
  ])

  // The active knock status location id backing this bubble (SH-661 clear). Prefer the id the
  // click handler resolved onto `data`; fall back to a matched live pin.
  const knockStatusLocationId =
    Number.isFinite(knockStatusLocationPinId) && knockStatusLocationPinId > 0
      ? knockStatusLocationPinId
      : (matchedContactLocation?.knockStatusLocationId ?? null)

  return {
    addressDisplay,
    entityType,
    entityTypeDisplay,
    fallbackLocation,
    formattedPhone,
    nameAndCompanyDisplay,
    selectedEntity,
    serviceLocation,
    variant,
    entityStatus,
    fallbackEntityDetailsStatus,
    status,
    knockStatus,
    canViewHistory,
    knockStatusPreview,
    knockStatusLocationId,
  }
}
